/**
 * Cranford Publications - Celtic Fiddle Music E-commerce App
 * Main Application Component
 *
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: November 5, 2025
 * Version: 2.01
 */

import React, { useState, useEffect } from 'react';
import './App.css';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { VisibilityProvider } from './context/VisibilityContext';
import Header from './components/Header';
import CartPanel from './components/CartPanel';
import AdminSettings from './components/AdminSettings';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './components/Home';
import Shop from './components/Shop';
import About from './components/About';
import Developer from './components/Developer';
import Contact from './components/Contact';
import Admin from './components/Admin';
import AdminProducts from './components/AdminProducts';
import AdminShipping from './components/AdminShipping';
import AdminUsers from './components/AdminUsers';
import AdminAssets from './components/AdminAssets';
import AdminVisibility from './components/AdminVisibility';
import AdminCategories from './components/AdminCategories';
import AdminPages from './components/AdminPages';
import AdminOrders from './components/AdminOrders';
import AdminOrderReminders from './components/AdminOrderReminders';
import AdminDocumentation from './components/AdminDocumentation';
import AdminBugReports from './components/AdminBugReports';
import AdminTemplates from './components/AdminTemplates';
import AdminTuneMetadata from './components/AdminTuneMetadata';
import Unsubscribe from './components/Unsubscribe';
import PageEditor from './components/PageEditor';
import PageViewer from './components/PageViewer';
import ProtectedPage from './components/ProtectedPage';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import Checkout from './components/Checkout';
import CheckoutSuccess from './components/CheckoutSuccess';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ProductDetail from './components/ProductDetail';
import Profile from './components/Profile';
import NewsletterDashboard from './components/NewsletterDashboard';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import ReturnPolicy from './components/ReturnPolicy';
import ShippingPolicy from './components/ShippingPolicy';
import FAQ from './components/FAQ';
import CookieConsent from './components/CookieConsent';
import { ToastProvider } from './components/ToastContainer';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import FloatingThemeWidget from './components/FloatingThemeWidget';
import FloatingMusicPlayer from './components/FloatingMusicPlayer';
import BugReportFloatingButton from './components/BugReportFloatingButton';
import Article from './components/Article';
import ArticlePage from './components/ArticlePage';
import Tunes from './components/Tunes';
import ABCAssetViewer from './components/ABCAssetViewer';
import ABCTool from './components/ABCTool';
import Information from './components/Information';
import LoadingTest from './components/LoadingTest';
import { HelmetProvider } from 'react-helmet-async';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import InstallAppBanner from './components/InstallAppBanner';
import usePageTracking from './hooks/usePageTracking';
import UserBugReportView from './components/UserBugReportView';
import AdvancedSearchPage from './components/AdvancedSearchPage';

/** React Router does not reset window scroll on navigation; scroll after pathname changes (e.g. related links from long pages). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const AppContent = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Track page views automatically
  usePageTracking();

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  // Header is always shown now
  return (
    <div className="App">
      <ScrollToTop />
      <Header onCartClick={toggleCart} />
      <EmailVerificationBanner />
      <main className="App-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<ProtectedPage pageKey="shop"><Shop /></ProtectedPage>} />
          <Route path="/products" element={<Navigate to="/shop" replace />} />
          <Route path="/login" element={<ProtectedPage pageKey="login"><Login /></ProtectedPage>} />
          <Route path="/register" element={<ProtectedPage pageKey="register"><Register /></ProtectedPage>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/checkout" element={<ProtectedPage pageKey="checkout"><Checkout /></ProtectedPage>} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
          <Route path="/admin/settings" element={<ProtectedAdminRoute><AdminSettings /></ProtectedAdminRoute>} />
          <Route path="/admin/products" element={<ProtectedAdminRoute><AdminProducts /></ProtectedAdminRoute>} />
          <Route path="/admin/shipping" element={<ProtectedAdminRoute><AdminShipping /></ProtectedAdminRoute>} />
          <Route path="/admin/categories" element={<ProtectedAdminRoute><AdminCategories /></ProtectedAdminRoute>} />
          <Route path="/admin/users" element={<ProtectedAdminRoute><AdminUsers /></ProtectedAdminRoute>} />
          <Route path="/admin/newsletter" element={<ProtectedAdminRoute><NewsletterDashboard /></ProtectedAdminRoute>} />
          <Route path="/admin/assets" element={<ProtectedAdminRoute><AdminAssets /></ProtectedAdminRoute>} />
          <Route path="/admin/visibility" element={<ProtectedAdminRoute><AdminVisibility /></ProtectedAdminRoute>} />
          <Route path="/admin/pages" element={<ProtectedAdminRoute><AdminPages /></ProtectedAdminRoute>} />
          <Route path="/admin/pages/edit" element={<ProtectedAdminRoute><PageEditor /></ProtectedAdminRoute>} />
          <Route path="/admin/orders" element={<ProtectedAdminRoute><AdminOrders /></ProtectedAdminRoute>} />
          <Route path="/admin/shop" element={<Navigate to="/shop" replace />} />
          <Route path="/admin/order-reminders" element={<ProtectedAdminRoute><AdminOrderReminders /></ProtectedAdminRoute>} />
          <Route path="/admin/documentation" element={<ProtectedAdminRoute><AdminDocumentation /></ProtectedAdminRoute>} />
          <Route path="/admin/bug-reports" element={<ProtectedAdminRoute><AdminBugReports /></ProtectedAdminRoute>} />
          <Route path="/admin/templates" element={<ProtectedAdminRoute><AdminTemplates /></ProtectedAdminRoute>} />
          <Route path="/admin/tune-metadata" element={<ProtectedAdminRoute><AdminTuneMetadata /></ProtectedAdminRoute>} />
          <Route path="/about" element={<ProtectedPage pageKey="about"><About /></ProtectedPage>} />
          <Route path="/developer" element={<ProtectedPage pageKey="developer"><Developer /></ProtectedPage>} />
          <Route path="/contact" element={<ProtectedPage pageKey="contact"><Contact /></ProtectedPage>} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/newsletter" element={<ProtectedRoute><NewsletterDashboard /></ProtectedRoute>} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/newsletter/unsubscribe" element={<Unsubscribe />} />
          <Route path="/search" element={<AdvancedSearchPage />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/information" element={<Information />} />
          <Route path="/article" element={<Navigate to="/articles" replace />} />
          <Route path="/articles" element={<Article />} />
          <Route path="/article/:category/:page" element={<ArticlePage />} />
          <Route path="/tunes" element={<Tunes />} />
          <Route path="/tune-asset/:id" element={<ABCAssetViewer />} />
          <Route path="/abc-tool" element={<ABCTool />} />
          <Route path="/page/:pageId" element={<PageViewer />} />
          <Route path="/loading-test" element={<LoadingTest />} />
          <Route path="/bug-report/:id" element={<UserBugReportView />} />
        </Routes>
      </main>
      <CartPanel isOpen={isCartOpen} onClose={toggleCart} />
      <CookieConsent />
      <InstallAppBanner />
      <BugReportFloatingButton />
      <FloatingThemeWidget />
      <FloatingMusicPlayer />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
        <ToastProvider>
          <AuthProvider>
            <VisibilityProvider>
              <CartProvider>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <AppContent />
                  <Footer />
                </Router>
              </CartProvider>
            </VisibilityProvider>
          </AuthProvider>
        </ToastProvider>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
